import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { isAnonymousPath } from './anonymousRoutes'
import { api, ApiError } from './api'
import { ArmsError } from './arms'
import { canSee, has, hasAny, scopeOf, type Scope } from './permissions'
import * as session from './session'
import type { CountryAccess, CurrentUser } from '@/types/api'

/** Where ARMS sends the browser back to. Registered on the client as a redirect URI. */
export const CALLBACK_PATH = '/auth/callback'

interface AuthState {
  user: CurrentUser | undefined
  /** True until the session has been restored, so nothing flashes the sign-in screen. */
  restoring: boolean
  /**
   * Set when Codemax authenticated the caller but QuoteHero would not have them - not
   * invited, or deactivated. It is not a sign-in failure and must not be answered with
   * another sign-in attempt.
   */
  refusal: ApiError | undefined
  /** Something went wrong talking to Codemax. Distinct from a refusal, which succeeded. */
  error: string | undefined
  signIn: () => Promise<void>
  signOut: () => Promise<void>
  /**
   * Ends the Codemax session and starts a new sign-in. The answer to a refusal: the
   * token was valid, so signing in again would fetch the same one from the same ARMS
   * cookie - the account itself has to change.
   */
  switchAccount: () => Promise<void>
  /** Re-reads the caller's permissions, after an administrator has changed them. */
  reload: () => Promise<void>
  can: (code: string) => boolean
  canAny: (...codes: string[]) => boolean
  canSeeAny: (resourceAction: string) => boolean
  scope: (resourceAction: string) => Scope
  countries: CountryAccess[]
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | undefined>(undefined)
  const [refusal, setRefusal] = useState<ApiError | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [restoring, setRestoring] = useState(true)
  const navigate = useNavigate()

  /**
   * Turns an ARMS token into a QuoteHero session.
   *
   * Called on every load rather than only after a sign-in, which is what makes it
   * self-healing: somebody invited after they first tried is bound on their next visit,
   * without an administrator having to talk them through anything.
   */
  const start = useCallback(async () => {
    try {
      const me = await api.post<CurrentUser>('/auth/session')
      setUser(me)
      setRefusal(undefined)
    } catch (cause) {
      if (cause instanceof ApiError && (cause.isForbidden || cause.status === 409)) {
        // Entitlement, not authentication. Sending them back to ARMS would return the
        // same valid token and start the loop again, so stop and say what happened.
        setRefusal(cause)
        setUser(undefined)
        return
      }

      throw cause
    }
  }, [])

  useEffect(() => {
    let live = true

    async function restore() {
      let leaving = false

      try {
        // Coming back from ARMS. Handled here rather than in a route because the app
        // must not decide anything about the session before the code is exchanged -
        // and a route would first have to get past the spinner this state drives.
        if (window.location.pathname === CALLBACK_PATH) {
          const returnTo = await completeSignIn(window.location.search)

          // Through the router, not history.replaceState. Both change the address bar,
          // but only this one tells the router where it now is: replacing the history
          // entry behind its back leaves it still rendering /auth/callback, which is
          // not a route, so a successful sign-in lands on "that page does not exist".
          //
          // Replace rather than push, so Back does not return to a redeemed code.
          navigate(returnTo, { replace: true })

          await start()
          return
        }

        if (session.hasSession()) {
          await start()
          return
        }

        // The customer's pages ask ARMS nothing. A browser that is already signed in gets
        // a full-page redirect for its answer, and on these pages that redirect is pure
        // cost: it kills the quotation request already in flight - which the customer sees
        // as "Could not reach the server" until the round trip returns - to learn something
        // the page will not consult. A customer has no ARMS account and never will.
        if (isAnonymousPath(window.location.pathname)) {
          return
        }

        // Nothing in memory, which is every reload. Ask ARMS whether this browser is
        // already signed in; if it is, the answer is a redirect and this page is on its
        // way out. If it is not, fall through to the sign-in screen.
        leaving = await session.restore()
      } catch (cause) {
        session.clearSession()

        // login_required is not a fault - it is what a first visit looks like, and the
        // sign-in screen is the right answer to it.
        if (cause instanceof ArmsError && !cause.needsInteraction) {
          if (live) setError(cause.message)
        } else if (!(cause instanceof ArmsError)) {
          if (live) setError('Could not reach Codemax sign-in. Check your connection and try again.')
        }
      } finally {
        // Not while the browser is already navigating away: flipping this would flash
        // the sign-in screen on top of a redirect that is about to succeed.
        if (live && !leaving) setRestoring(false)
      }
    }

    void restore()
    return () => {
      live = false
    }
  }, [start, navigate])

  const signIn = useCallback(() => session.signIn(), [])

  const signOut = useCallback(async () => {
    setUser(undefined)
    setRefusal(undefined)
    setError(undefined)
    await session.signOut()
  }, [])

  const switchAccount = useCallback(async () => {
    setUser(undefined)
    setRefusal(undefined)
    setError(undefined)
    await session.switchAccount()
  }, [])

  const reload = useCallback(async () => {
    const me = await api.get<CurrentUser>('/auth/me')
    setUser(me)
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      restoring,
      refusal,
      error,
      signIn,
      signOut,
      switchAccount,
      reload,
      can: (code) => has(user?.permissions ?? [], code),
      canAny: (...codes) => hasAny(user?.permissions ?? [], ...codes),
      canSeeAny: (resourceAction) => canSee(user?.permissions ?? [], resourceAction),
      scope: (resourceAction) => scopeOf(user?.permissions ?? [], resourceAction),
      countries: user?.countries ?? [],
    }),
    [user, restoring, refusal, error, signIn, signOut, switchAccount, reload],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

async function completeSignIn(search: string): Promise<string> {
  const { returnTo } = await session.completeCallback(search)
  return returnTo
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')

  return context
}
